import React, { useState, useEffect } from "react";
import axios from "axios";
import styles from "../../assets/styles/Homepage/WhyChooseUs.module.css";
import { FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const containerVariant = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } },
};

const itemVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const headerVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const modalVariant = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const modalContentVariant = {
  hidden: { y: 60, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { type: "spring", stiffness: 260, damping: 28 } },
  exit: { y: 60, opacity: 0, transition: { duration: 0.18 } },
};

const WhyChooseUs = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [headingData, setHeadingData] = useState(null);
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [headingRes, cardsRes] = await Promise.all([
          axios.get(`${ENV_API_BASE_URL}/api/why-choose-heading`),
          axios.get(`${ENV_API_BASE_URL}/api/homewhychoosecard`),
        ]);
        const headings = headingRes.data.data;
        if (Array.isArray(headings) && headings.length > 0) setHeadingData(headings[0]);
        const cards = cardsRes.data.data;
        if (Array.isArray(cards)) setCardData([...cards].reverse());
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const openModal = (card) => {
    setSelectedCard(card);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setSelectedCard(null);
    document.body.style.overflow = "unset";
  };

  if (loading) {
    return (
      <section className={styles.section}>
        <div className={styles.loaderWrap}>
          <div className={styles.loaderDot} />
          <div className={styles.loaderDot} />
          <div className={styles.loaderDot} />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>

      {/* ── HEADER ── */}
      <motion.div
        className={styles.header}
        variants={headerVariant}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <p className={styles.tagline}>— WHY CHOOSE US</p>
        <h2 className={styles.heading}>
          {headingData?.mainHeading || "Why Choose Us?"}
        </h2>
        <div className={styles.headerLine} />
        <p className={styles.subText}>
          {headingData?.subHeading || ""}
        </p>
      </motion.div>

      {/* ── CARD GRID ── */}
      {cardData.length > 0 && (
        <motion.div
          className={styles.grid}
          variants={containerVariant}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {cardData.map((item, index) => (
            <motion.article
              key={item._id || index}
              className={`${styles.card} ${hoveredIndex !== null && hoveredIndex !== index ? styles.cardDim : ""}`}
              variants={itemVariant}
              onClick={() => openModal(item)}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              whileHover={{ y: -6 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              {/* ── Icon ── */}
              <div className={styles.iconWrap}>
                <span className={styles.iconEmoji}>{item.icon}</span>
              </div>

              {/* ── Heading & Subheading only ── */}
              <h3 className={styles.cardTitle}>{item.heading}</h3>
              {item.subheading && (
                <p className={styles.cardSubheading}>{item.subheading}</p>
              )}

              {/* ── Click hint ── */}
              <div className={styles.cardHint}>
                <span>View details →</span>
              </div>

              {/* ── Bottom accent bar ── */}
              <div className={styles.cardBar} />
            </motion.article>
          ))}
        </motion.div>
      )}

      {/* ── MODAL — shows image + heading + subheading + paragraph on click ── */}
      <AnimatePresence>
        {selectedCard && (
          <motion.div
            className={styles.overlay}
            variants={modalVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={closeModal}
          >
            <motion.div
              className={styles.modal}
              variants={modalContentVariant}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button className={styles.closeBtn} onClick={closeModal}>
                <FaTimes />
              </button>

              {/* Image panel */}
              <div className={styles.modalLeft}>
                {selectedCard.image ? (
                  <>
                    <img
                      src={`${ENV_API_BASE_URL}/${selectedCard.image}`}
                      alt={selectedCard.heading}
                      className={styles.modalImg}
                    />
                    <div className={styles.modalImgTint} />
                  </>
                ) : (
                  <div className={styles.modalNoImg}>
                    <span className={styles.modalBigIcon}>{selectedCard.icon}</span>
                  </div>
                )}
                <div className={styles.modalImgLabel}>
                  <span>{selectedCard.heading}</span>
                </div>
              </div>

              {/* Content panel: heading + subheading + paragraph */}
              <div className={styles.modalRight}>
                <span className={styles.modalTag}>Why Choose Us</span>

                <div className={styles.modalIconRow}>
                  <span className={styles.modalIcon}>{selectedCard.icon}</span>
                </div>

                <h2 className={styles.modalTitle}>{selectedCard.heading}</h2>

                {selectedCard.subheading && (
                  <p className={styles.modalSub}>{selectedCard.subheading}</p>
                )}

                <hr className={styles.modalHr} />

                <p className={styles.modalDesc}>{selectedCard.paragraph}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default WhyChooseUs;